"use client";

import {
  Children,
  createContext,
  type ReactNode,
  use,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "~/lib/utils";

const INTERVAL_MS = 3000;

const ActiveIndex = createContext(0);

/**
 * Owns the hero's rotation timer so every `Rotating` inside it advances in
 * step. Renders the wrapping div so hovering or focusing anywhere in the hero
 * pauses it.
 * Holds the first item with reduced motion and while the tab is hidden.
 */
export function HeroRotation({
  count,
  className,
  children,
}: {
  count: number;
  className?: string;
  children: ReactNode;
}) {
  const [active, setActive] = useState(0);
  // Hover and focus pause independently, so leaving with the pointer doesn't
  // resume while someone is still typing in the hero's address search.
  const hovered = useRef(false);
  const focused = useRef(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer: number | undefined;
    const sync = () => {
      window.clearInterval(timer);
      if (reduce.matches) {
        setActive(0);
        return;
      }
      timer = window.setInterval(() => {
        if (document.hidden || hovered.current || focused.current) return;
        setActive((i) => (i + 1) % count);
      }, INTERVAL_MS);
    };
    sync();
    reduce.addEventListener("change", sync);
    return () => {
      window.clearInterval(timer);
      reduce.removeEventListener("change", sync);
    };
  }, [count]);

  return (
    <ActiveIndex value={active}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: listens for hover and focus bubbling from the hero's own controls to pause rotation; the div is a grouping wrapper, not a control. */}
      <div
        className={className}
        onPointerEnter={() => {
          hovered.current = true;
        }}
        onPointerLeave={() => {
          hovered.current = false;
        }}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            focused.current = false;
          }
        }}
      >
        {children}
      </div>
    </ActiveIndex>
  );
}

/**
 * Crossfades through its children, one per rotation step. Every child is in
 * the server HTML, stacked in one grid cell so the box always fits the largest
 * and rotation never moves what's around it. Only classes change: the first
 * child is the one visible before hydration and to assistive tech, since the
 * rest are `aria-hidden` for good.
 */
export function Rotating({ children }: { children: ReactNode }) {
  const active = use(ActiveIndex);
  return (
    <span className="grid">
      {Children.map(children, (child, i) => (
        <span
          aria-hidden={i === 0 ? undefined : true}
          className={cn(
            "[grid-area:1/1] transition-opacity duration-(--dur-slow) motion-reduce:transition-none",
            i !== active && "opacity-0",
          )}
        >
          {/* Keeps plain-text extractors from running items together. It
              leads a line, so it never renders. */}
          {i > 0 && " "}
          {child}
        </span>
      ))}
    </span>
  );
}
